// The eight EB-1A criteria that apply to researchers — the single list every
// screen counts against (scan verdict, Case Plan line, Evidence & Criteria).
//
// Mirrors app/services/criterion_mapping/researcher_criteria.py; the backend
// test tests/test_criteria_catalog_sync.py fails if ids, letters or
// CRITERIA_TOTAL drift. Keep the `id:` / `letter:` lines one per entry.
//
// The regulation lists ten criteria; artistic exhibitions and commercial
// success in the performing arts never apply to scientists and are not
// shown. Older scan payloads used different ids (`press`, `contributions`,
// `role`, `salary`…) — `canonicalCriterionId` folds those in.

export interface CriterionCatalogEntry {
  id: string;
  letter: string;
  /** Short label for chips, rows and the verdict. */
  label: string;
  /** Rail / stepper label. */
  short: string;
  /** Full regulatory-flavoured name used as the step heading. */
  name: string;
  lead: string;
  help: string;
  /** One sentence: what turns "likely" into "met". */
  confirm: string;
  legacyIds: string[];
}

export const CRITERIA_CATALOG: CriterionCatalogEntry[] = [
  {
    id: "awards",
    letter: "A",
    label: "Awards & prizes",
    short: "Awards",
    name: "Awards for excellence",
    lead: "Nationally or internationally recognized prizes judged on excellence.",
    help: "USCIS weighs who grants the award, how selective it is, and whether it's nationally or internationally significant.",
    confirm: "Attach one award letter or fellowship notice",
    legacyIds: [],
  },
  {
    id: "membership",
    letter: "B",
    label: "Selective memberships",
    short: "Memberships",
    name: "Selective memberships",
    lead: "Memberships that require outstanding achievement, judged by experts.",
    help: "What matters is whether membership requires outstanding achievement judged by recognized experts — not dues or a degree.",
    confirm: "Attach a membership confirmation that shows admission is judged on achievement",
    legacyIds: ["memberships"],
  },
  {
    id: "published_material",
    letter: "C",
    label: "Press coverage",
    short: "Press coverage",
    name: "Published material about you (press coverage)",
    lead: "Coverage about you and your work in professional or major media.",
    help: "Articles in professional, major trade, or major media outlets about you and your work — capture the title, date, author, and outlet.",
    confirm: "Add a link to one article about your work",
    legacyIds: ["press", "published"],
  },
  {
    id: "judging",
    letter: "D",
    label: "Judging others' work",
    short: "Judging",
    name: "Judging the work of others",
    lead: "Serving as a judge of others' work — peer review, panels, committees.",
    help: "Journal peer review, editorial boards, grant or thesis panels all count. Volume strengthens the final-merits story.",
    confirm: "Attach one peer-review invitation or thank-you email",
    legacyIds: [],
  },
  {
    id: "original_contributions",
    letter: "E",
    label: "Original contributions",
    short: "Contributions",
    name: "Original contributions of major significance",
    lead: "Original work whose influence is visible beyond your own circle.",
    help: "Shown through citations, independent adoption, licensing, or patents validated by groups outside your training environment.",
    confirm: "Confirm your citation record and add papers that build on your work",
    legacyIds: ["contributions", "original_contribution"],
  },
  {
    id: "authorship",
    letter: "F",
    label: "Scholarly articles",
    short: "Authorship",
    name: "Scholarly authorship",
    lead: "Authorship of scholarly articles in your field.",
    help: "Articles in professional journals or other major media. The reach of the journals strengthens the claim.",
    confirm: "Confirm your publication list",
    legacyIds: [],
  },
  {
    id: "leading_role",
    letter: "G",
    label: "Leading or critical role",
    short: "Leading role",
    name: "Leading or critical role",
    lead: "A leading or critical role for a distinguished organization.",
    help: "Weighs the importance of your role and the standing of the organization or distinguished division.",
    confirm: "Pick the role on your CV that was critical to the organization",
    legacyIds: ["role", "critical_role"],
  },
  {
    id: "high_salary",
    letter: "H",
    label: "High salary",
    short: "Salary",
    name: "High salary",
    lead: "Pay significantly high relative to others in your field.",
    help: "Needs comparative wage data (BLS / OFLC or a credible survey) showing your pay sits well above the field median.",
    confirm: "Enter your base pay so we can compare it with BLS data",
    legacyIds: ["salary"],
  },
];

export const CRITERIA_TOTAL = 8;
export const CRITERIA_REQUIRED = 3;
export const CRITERIA_IDS: string[] = CRITERIA_CATALOG.map((c) => c.id);

const BY_ID: Record<string, CriterionCatalogEntry> = Object.fromEntries(
  CRITERIA_CATALOG.map((c) => [c.id, c]),
);
const ALIASES: Record<string, string> = Object.fromEntries(
  CRITERIA_CATALOG.flatMap((c) => c.legacyIds.map((a) => [a, c.id])),
);

/** Canonical id for any scan / state id, or null for ids we no longer show. */
export function canonicalCriterionId(raw: string | null | undefined): string | null {
  const key = (raw || "").trim().toLowerCase();
  if (!key) return null;
  if (BY_ID[key]) return key;
  return ALIASES[key] || null;
}

export function criterionEntry(raw: string | null | undefined): CriterionCatalogEntry | null {
  const id = canonicalCriterionId(raw);
  return id ? BY_ID[id] : null;
}

export function criterionLabel(raw: string): string {
  const e = criterionEntry(raw);
  if (e) return e.label;
  return (raw || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function confirmHintFor(raw: string): string {
  return criterionEntry(raw)?.confirm || "Confirm this with one document";
}
